/** Every capability, for exhaustiveness checks and for the Property 2 generator. */
export const CAPABILITIES = Object.freeze([
    'initiative:read',
    'initiative:write',
    'initiative:confidentiality',
    'masterdata:write',
    'masterdata:admin',
    'export:full',
    'export:filtered',
    'admin:restore',
    'admin:roles',
    'admin:ops',
    'manpower:read',
    'manpower:write',
    'capacity:write',
    'governance:read',
    'governance:write',
    'admin:backup',
]);
/**
 * The static role-to-capability table.
 *
 * Each row cites the criterion that grants the capability, because these rows
 * are the machine-readable form of Requirement 2 and a row added without a
 * citation is an unauthorized grant.
 *
 * Two absences are load-bearing:
 *
 * - `system_administrator` holds no `initiative:*` and no `export:*`
 *   capability. R2.7 withholds every Initiative field value from screens,
 *   search results, aggregates and exports for a user holding only that role.
 *   The RLS predicate omits the role for the same reason, so the withholding is
 *   structural at both layers rather than a screen-level rule. A System
 *   Administrator who also needs to read Initiatives is given a second role,
 *   and R2.9's union then grants the read.
 * - The five Read_Only_User roles hold no `initiative:write`. R2.5 requires a
 *   write attempt from them to be rejected with nothing changed; the route
 *   check rejects it before the handler opens a write.
 */
export const ROLE_CAPABILITIES = Object.freeze({
    /**
     * R2.2, R2.3: identical rights for every member; R3.8/R3.9; R6.7-R6.11;
     * R14.1; R15.1; `complan-manpower` R2, R3, R4, R7.7/R7.8 - Commercial
     * Planning holds every Manpower capability, including the Effort Matrix
     * write access Field Force Leadership is withheld (Assumption A4).
     */
    commercial_planning: Object.freeze([
        'initiative:read',
        'initiative:write',
        'initiative:confidentiality',
        'masterdata:write',
        'export:full',
        'export:filtered',
        'manpower:read',
        'manpower:write',
        'capacity:write',
        'governance:read',
        'governance:write',
    ]),
    /**
     * R2.4: read within the assigned Platform scope, plus Input_Request; R15.1
     * export of that view; `complan-manpower` R7.7 - read-only access to the
     * two aggregate Manpower screens like every other Read_Only_User role;
     * `complan-governance` R3.1/R4.1 - likewise for the two dashboards.
     */
    platform_owner: Object.freeze([
        'initiative:read',
        'export:filtered',
        'manpower:read',
        'governance:read',
    ]),
    /** R2.4: read within the assigned Channel scope; `complan-manpower` R7.7; `complan-governance` R3.1. */
    channel_owner: Object.freeze([
        'initiative:read',
        'export:filtered',
        'manpower:read',
        'governance:read',
    ]),
    /**
     * R2.4: read every Initiative whose Field Force involvement is yes;
     * `complan-manpower` R7.8 - the one Read_Only_User role that also holds
     * `capacity:write` (Capacity_Period maintenance), per
     * `complan-manpower/design.md`'s capability table.
     */
    field_force_leadership: Object.freeze([
        'initiative:read',
        'export:filtered',
        'manpower:read',
        'capacity:write',
        'governance:read',
    ]),
    /** R2.4: read every Initiative whose DTE or CSC involvement is yes; `complan-manpower` R7.7; `complan-governance` R3.1. */
    dte_csc_stakeholder: Object.freeze([
        'initiative:read',
        'export:filtered',
        'manpower:read',
        'governance:read',
    ]),
    /**
     * R2.4: read every Initiative of the retained planning years;
     * `complan-manpower` R7.7; `complan-governance` R4.1 - the
     * Management_Dashboard is this role's primary screen (PRD section 4: "KPIs,
     * annual calendar and risks"), and Assumption A13 records that it therefore
     * sees the widest Viewer_Scoped figures short of Commercial Planning's.
     */
    management_viewer: Object.freeze([
        'initiative:read',
        'export:filtered',
        'manpower:read',
        'governance:read',
    ]),
    /**
     * R16.1-R16.4, R8.9, R1.9, R9.10, R17.6, R17.10 - and no Initiative content
     * (R2.7), and no Manpower capability either: `complan-manpower/design.md`'s
     * capability table grants `manpower:read`/`manpower:write`/`capacity:write`
     * to Commercial Planning, Field Force Leadership and the Read_Only_User
     * roles alone, mirroring the same reasoning R2.7 states for Initiative
     * content - a System Administrator's role is platform administration, not
     * planning data of any kind.
     *
     * `complan-governance` withholds `governance:read` for the same reason, and
     * R4.10 states it directly: every Governance_Metric and Governance_Visual is
     * derived from Initiative records, so granting the dashboards to a role that
     * may read no Initiative would hand it aggregates over data it is denied
     * record by record. `governance:write` is withheld too - a Watchout is
     * Initiative annotation (R7.7), which is content.
     */
    system_administrator: Object.freeze([
        'masterdata:write',
        'masterdata:admin',
        'admin:restore',
        'admin:roles',
        'admin:ops',
    ]),
});
/**
 * The capabilities a single role grants.
 *
 * An unrecognized role contributes nothing, mirroring R1.2's rule for group
 * memberships the mapping does not name: a stored role assignment naming a role
 * this release does not know must not widen access.
 */
export function capabilitiesForRole(role) {
    return ROLE_CAPABILITIES[role] ?? [];
}
/**
 * The capability set of a role set: the union of the member roles' capabilities
 * (R1.8, R2.9).
 *
 * The empty role set yields the empty capability set, which is the zero-role
 * principal of R1.3 as a first-class value rather than an error path: it reaches
 * no capability-gated route, and RLS returns it zero rows.
 *
 * Duplicate roles in the input are absorbed by the set, so the caller need not
 * deduplicate stored role assignments first.
 */
export function resolveCapabilities(roles) {
    const capabilities = new Set();
    for (const role of roles) {
        for (const capability of capabilitiesForRole(role)) {
            capabilities.add(capability);
        }
    }
    return capabilities;
}
/**
 * Capabilities granted only to a principal holding *every* listed role.
 *
 * Kept apart from {@link ROLE_CAPABILITIES} so that table and
 * {@link resolveCapabilities} keep their literal-union meaning (R1.8, R2.9).
 * A conjunctive grant is still monotone: adding a role can only add
 * capabilities, never remove one.
 *
 * `admin:backup`: a complete backup contains every Initiative field value,
 * which R2.7 withholds from a System Administrator alone. Requiring Commercial
 * Planning on the same account means the content owner's role signs off on
 * each person who may export it. Migration 30's export functions check the
 * same pair of roles, so the rule holds in the database as well.
 */
export const CONJUNCTIVE_CAPABILITIES = Object.freeze([
    Object.freeze({
        capability: 'admin:backup',
        requiresAllOf: Object.freeze(['system_administrator', 'commercial_planning']),
    }),
]);
/**
 * The full capability set of a principal: the role union of
 * {@link resolveCapabilities} plus every {@link CONJUNCTIVE_CAPABILITIES}
 * grant whose roles are all held. This is what the API enforces and reports
 * on `/api/me`.
 */
export function resolvePrincipalCapabilities(roles) {
    const held = new Set(roles);
    const capabilities = new Set(resolveCapabilities(held));
    for (const { capability, requiresAllOf } of CONJUNCTIVE_CAPABILITIES) {
        if (requiresAllOf.every((role) => held.has(role))) {
            capabilities.add(capability);
        }
    }
    return capabilities;
}
//# sourceMappingURL=capabilities.js.map